import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { HelpSection, FormulaBox, InfoItem, HelpList, Clock, Calendar, Briefcase, Calculator, FileText, Settings } from '../components/HelpComponents';
import { helpContentFr, helpSectionsFr, leaveExamples, sickStatuses } from '../components/HelpContent';
import { ClipboardCheck } from 'lucide-react';

export default function Help() {
  const { language } = useLanguage();
  const { user } = useAuth();
  
  const t = helpContentFr;
  const s = helpSectionsFr;

  return (
    <div className="space-y-6" data-testid="help-page">
      <div>
        <h1 className="text-3xl font-bold">{t.title}</h1>
        <p className="text-muted-foreground">{t.subtitle}</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <HelpSection icon={Clock} title={s.workHours.title}>
          <p className="text-sm text-muted-foreground">{s.workHours.desc}</p>
          <FormulaBox title={t.formulaLabel} formula={s.workHours.formula} example={s.workHours.example} />
          <HelpList items={s.workHours.details} />
        </HelpSection>

        <HelpSection icon={Calendar} title={s.leave.title}>
          <p className="text-sm text-muted-foreground">{s.leave.desc}</p>
          <FormulaBox title={t.formula1Label} formula={s.leave.formula} />
          <FormulaBox title={t.formula2Label} formula={s.leave.formula2} example={s.leave.example} />
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="font-medium text-sm mb-2">{t.examplesLabel}</p>
            <div className="space-y-1">
              {leaveExamples.map((ex, i) => (
                <InfoItem key={i} label={ex.hours} value={`${ex.day} (½ = ${ex.halfDay})`} />
              ))}
            </div>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <p className="text-sm text-amber-700 dark:text-amber-400">{s.leave.priority}</p>
          </div>
        </HelpSection>

        <HelpSection icon={Briefcase} title={s.sick.title}>
          <p className="text-sm text-muted-foreground">{s.sick.desc}</p>
          <FormulaBox title={t.formulaLabel} formula={s.sick.formula} />
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
            <p className="text-sm text-blue-700 dark:text-blue-400">{s.sick.note}</p>
          </div>
          <div className="space-y-1">
            <p className="font-medium text-sm">{t.statusesLabel}</p>
            {sickStatuses.map((st, i) => (
              <InfoItem key={i} label={st.status} value={st.desc} />
            ))}
          </div>
        </HelpSection>

        <HelpSection icon={Calculator} title={s.recovery.title}>
          <p className="text-sm text-muted-foreground">{s.recovery.desc}</p>
          <FormulaBox title={t.formulaLabel} formula={s.recovery.formula} example={s.recovery.example} />
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-sm text-muted-foreground">{s.recovery.note}</p>
          </div>
        </HelpSection>

        <HelpSection icon={FileText} title={s.planning.title}>
          <HelpList items={s.planning.items} />
        </HelpSection>

        <HelpSection icon={FileText} title={s.planningTotals.title}>
          <p className="text-sm text-muted-foreground mb-4">{s.planningTotals.desc}</p>
          
          <div className="mb-4">
            <h4 className="font-medium text-sm mb-2 text-primary">Vue Semaine (verticale)</h4>
            <ul className="space-y-1 text-sm">
              <li className="flex items-start gap-2"><span className="text-primary">•</span>Total Semaine : Collaborateurs présents sur la semaine sélectionnée avec leurs heures</li>
              <li className="flex items-start gap-2"><span className="text-primary">•</span>Total Mois : Collaborateurs présents sur le mois en cours avec leurs heures</li>
            </ul>
          </div>
          
          <div className="mb-4">
            <h4 className="font-medium text-sm mb-2 text-primary">Vue Mois (verticale)</h4>
            <ul className="space-y-1 text-sm">
              <li className="flex items-start gap-2"><span className="text-primary">•</span>Total Mois : Collaborateurs présents sur le mois affiché avec leurs heures</li>
              <li className="flex items-start gap-2"><span className="text-primary">•</span>Calcul identique au total mois de la vue semaine</li>
            </ul>
          </div>
          
          <div className="mb-4">
            <h4 className="font-medium text-sm mb-2 text-primary">Vue Horizontale</h4>
            <ul className="space-y-1 text-sm">
              <li className="flex items-start gap-2"><span className="text-primary">•</span>Total Semaine : Collaborateurs de la première semaine visible</li>
              <li className="flex items-start gap-2"><span className="text-primary">•</span>Total Mois : Collaborateurs présents sur le mois en cours</li>
            </ul>
          </div>
          
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
            <p className="text-sm text-blue-700 dark:text-blue-400">Les totaux sont automatiquement filtrés selon les collaborateurs sélectionnés.</p>
          </div>
        </HelpSection>

        <HelpSection icon={Settings} title={s.admin.title}>
          <HelpList items={s.admin.items} />
        </HelpSection>

        <HelpSection icon={ClipboardCheck} title={s.dailyValidation.title}>
          <p className="text-sm text-muted-foreground">{s.dailyValidation.desc}</p>
          <HelpList items={s.dailyValidation.items} />
        </HelpSection>

        <HelpSection icon={ClipboardCheck} title={s.managerValidation.title}>
          <p className="text-sm text-muted-foreground">{s.managerValidation.desc}</p>
          <HelpList items={s.managerValidation.items} />
        </HelpSection>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <div className="bg-primary/10 px-4 py-3 border-b border-border">
          <h2 className="font-semibold text-lg">{t.summaryTitle}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-2 font-medium">{t.typeCol}</th>
                <th className="text-left px-4 py-2 font-medium">{t.formulaCol}</th>
                <th className="text-left px-4 py-2 font-medium">{t.baseCol}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border">
                <td className="px-4 py-2 font-medium">{t.workType}</td>
                <td className="px-4 py-2 font-mono text-xs">(fin - début) - pause</td>
                <td className="px-4 py-2 text-muted-foreground">{t.slotBase}</td>
              </tr>
              <tr className="border-t border-border">
                <td className="px-4 py-2 font-medium">{t.leaveType}</td>
                <td className="px-4 py-2 font-mono text-xs">jours × (heures_contract / 5)</td>
                <td className="px-4 py-2 text-muted-foreground">{t.contractBase}</td>
              </tr>
              <tr className="border-t border-border">
                <td className="px-4 py-2 font-medium">{t.sickType}</td>
                <td className="px-4 py-2 font-mono text-xs">jours × (heures_contract / 5)</td>
                <td className="px-4 py-2 text-muted-foreground">{t.contractBase}</td>
              </tr>
              <tr className="border-t border-border">
                <td className="px-4 py-2 font-medium">{t.recoveryType}</td>
                <td className="px-4 py-2 font-mono text-xs">heures_réelles - heures_planifiées</td>
                <td className="px-4 py-2 text-muted-foreground">{t.reportBase}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
